import type React from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  TourInDepthTab,
  TourMegaTab,
  TourSelectMode,
  TourSelection,
  TourStep,
} from './tourSteps';
import { hasSeenTour, markTourSeen } from './tourStorage';

/**
 * What the active report slide exposes to the tour: its DOM root (target
 * lookups are scoped to it, so the carousel's neighbor slides never match),
 * imperative tab/selection switches, and getters for the state it owns.
 *
 * Every member MUST be referentially stable for the life of the slide — the
 * frame is a dependency of the tour's step choreography, so a frame that
 * changed identity on each tab switch would restart the step mid-move. The
 * getters exist for exactly that reason: they read refs instead of closing
 * over state, which keeps the frame stable while still reading fresh values.
 */
export interface TourFrame {
  root: HTMLElement;
  getMegaTab: () => TourMegaTab;
  setMegaTab: (tab: TourMegaTab) => void;
  getInDepthTab: () => TourInDepthTab;
  setInDepthTab: (tab: TourInDepthTab) => void;
  /** The roster selector's Players / Teams mode (Em Detalhe only). */
  getMode: () => TourSelectMode;
  setMode: (mode: TourSelectMode) => void;
  /** The Shots tab's shot-type chip ('all' for every stroke). */
  getShotType: () => string;
  setShotType: (shotType: string) => void;
  /** Resolve a step's named selection against this slide's roster. */
  selectionTarget: (kind: TourSelection) => string[];
  getSelection: () => string[];
  setSelection: (ids: string[]) => void;
}

export interface TourController {
  steps: TourStep[];
  /** null when idle; the current step index while running. */
  stepIndex: number | null;
  frame: TourFrame | null;
  start: () => void;
  /** End the tour (finished or skipped) and never auto-start again. */
  dismiss: () => void;
  next: () => void;
  prev: () => void;
  /** Drop the current step in the given direction (unresolvable target). */
  skipStep: (dir: 1 | -1) => void;
  registerFrame: (frame: TourFrame) => () => void;
}

const TourContext = createContext<TourController | null>(null);

/** The tour controller, or null outside a provider (the /tournament route). */
export function useTour(): TourController | null {
  return useContext(TourContext);
}

/**
 * Breathing room between "the report is on screen" and the dim arriving. The
 * report is fully painted by then (it rendered under the splash), so this is
 * purely so the overlay doesn't feel like it ambushed the first look.
 */
const AUTO_START_DELAY_MS = 800;

export const TourProvider: React.FC<{
  steps: TourStep[];
  /**
   * Whether the report is actually the thing on screen. False while the splash
   * is still up or fading: the report mounts underneath it, so a frame
   * registering is NOT proof the user can see anything yet, and auto-starting
   * on it alone put the tour on top of the splash animation.
   */
  ready?: boolean;
  children: React.ReactNode;
}> = ({ steps, ready = true, children }) => {
  const [stepIndex, setStepIndex] = useState<number | null>(null);
  const [frame, setFrame] = useState<TourFrame | null>(null);
  const running = stepIndex !== null;
  const runningRef = useRef(running);
  runningRef.current = running;

  const start = useCallback(() => setStepIndex(0), []);

  const dismiss = useCallback(() => {
    setStepIndex(null);
    markTourSeen();
  }, []);

  const stepCount = steps.length;
  const move = useCallback(
    (dir: 1 | -1) =>
      setStepIndex((i) => {
        if (i === null) return i;
        const next = i + dir;
        if (next >= stepCount) {
          // walked off the end — the tour is done
          markTourSeen();
          return null;
        }
        return Math.max(0, next);
      }),
    [stepCount],
  );
  const next = useCallback(() => move(1), [move]);
  const prev = useCallback(() => move(-1), [move]);

  // Auto-start once per device, a beat after the first report slide registers
  // AND the splash has cleared out of the way.
  useEffect(() => {
    if (!frame || !ready || runningRef.current || hasSeenTour()) return;
    const id = setTimeout(() => {
      if (!runningRef.current) setStepIndex(0);
    }, AUTO_START_DELAY_MS);
    return () => clearTimeout(id);
  }, [frame, ready]);

  const registerFrame = useCallback((f: TourFrame) => {
    setFrame(f);
    return () => setFrame((cur) => (cur === f ? null : cur));
  }, []);

  const value = useMemo<TourController>(
    () => ({
      steps,
      stepIndex,
      frame,
      start,
      dismiss,
      next,
      prev,
      skipStep: move,
      registerFrame,
    }),
    [steps, stepIndex, frame, start, dismiss, next, prev, move, registerFrame],
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};
